<template>
  <div
    class="map-wrap"
    :style="
      'width: ' +
      (width ? width : '100%') +
      '; height: ' +
      (height ? height : '100%')
    "
  >
    <!-- Markers -->
    <div
      v-for="(marker, index) of markers"
      class="map-marker d-flex-col"
      :key="'map-marker-' + index"
      :ref="'map-marker-' + index"
    >
      <div
        class="map-marker-wrapper"
        :class="{
          selected: marker.selected,
          hidden: showGroupedMarkers,
          disabled: marker.disabled,
        }"
      >
        <Tooltip
          :hoverOnly="true"
          :disabled="!marker.disabled"
          class="full-width"
        >
          <template #target>
            <div
              class="map-marker-content"
              @click="if (!marker.disabled) $emit('clickMapMarker', marker);"
            >
              <Logo :logo="marker.provider" class="mx-auto" />
              <div class="map-marker-content-city token-text-mediumbold">
                {{ marker.city }}
              </div>
              <div
                v-if="marker.price !== Infinity"
                class="map-marker-content-price token-text-medium no-text-wrap"
              >
                <template v-if="marker.price">{{
                  "From " +
                  $helpers.prices.format(marker.price, marker.currency, 0)
                }}</template>
                <template v-else><Spinner :light="marker.selected" /></template>
              </div>
              <div class="map-marker-background"></div>
            </div>
          </template>
          <template #content>
            This location is temporarily unavailable.<br />
            This can be due to a lack of server availability, or you might need
            to connect your own account for that cloud provider.
          </template>
        </Tooltip>
        <div class="map-marker-point mt-1 mx-auto"></div>
      </div>
    </div>
    <!-- / Markers -->

    <!-- Group Markers -->
    <div
      v-for="(marker, index) of groupMarkers"
      class="map-marker d-flex-col"
      :key="'map-marker-' + index"
      :ref="'map-group-marker-' + index"
    >
      <div
        class="map-marker-wrapper"
        :class="{ selected: marker.selected, hidden: !showGroupedMarkers }"
      >
        <div class="map-group-marker-size">
          {{ marker.groupSize }}
        </div>
        <div
          v-for="i in Math.min(marker.groupSize || 1, 3)"
          :key="`map-marker-spacer-${index}-${i}`"
          :class="`map-marker-content map-marker-content-${i}`"
          @click="
            onSelectCountry({
              text: marker.city,
              value: marker.id,
              meta: {
                latitude: marker.lat,
                longitude: marker.lng,
              },
            })
          "
        >
          <div class="mx-auto d-flex">
            <Logo
              v-for="provider of marker.provider"
              :key="`${marker.id}-${provider}`"
              :logo="provider"
              class="fit-content"
            />
          </div>
          <div class="map-marker-content-city token-text-mediumbold">
            {{ marker.city }}
          </div>
          <div
            class="map-marker-content-price token-text-medium no-text-wrap"
            v-if="marker.price !== Infinity"
          >
            <template v-if="marker.price">{{
              "From " + $helpers.prices.format(marker.price, marker.currency, 0)
            }}</template>
            <template v-else><Spinner :light="marker.selected" /></template>
          </div>
        </div>
        <div class="map-marker-point mt-1 mx-auto"></div>
      </div>
    </div>
    <!-- / Group Markers -->

    <!-- Search country -->
    <div ref="searchCountry">
      <CountrySelector
        :selectedCountry="selectedCountry"
        @select="onSelectCountry($event)"
      />
    </div>
    <!-- / Search country -->

    <!-- Map -->
    <div>
      <div id="map" class="map" />
    </div>
    <!-- / Map -->
  </div>
</template>

<script lang="ts">
import { Options, Vue, prop } from "vue-class-component";
import {
  Map as MapLibre,
  NavigationControl,
  Marker,
  GeolocateControl,
  IControl,
} from "maplibre-gl";
import { Watch } from "vue-property-decorator";
import { MapMarker } from "@/types";
import { ListItem } from "@/components/UIElements//List.vue";
import countriesList from "@/assets/countries/list.json";

import Logo from "@/components/UIElements/Logo.vue";
import Spinner from "@/components/UIElements/Spinner.vue";
import CustomSelect from "@/components/UIElements/CustomSelect.vue";
import CountrySelector from "@/components/UIElements/CountrySelector.vue";
import Tooltip from "@/components/UIElements/Tooltip.vue";

class SearchCountryControl implements IControl {
  _map: MapLibre | undefined;
  _container: HTMLElement;
  constructor(container: HTMLElement) {
    this._container = container;
    this._container.className = "maplibregl-ctrl search-country-control";
  }
  onAdd(map: MapLibre) {
    this._map = map;
    return this._container;
  }

  onRemove() {
    if (this._container?.parentNode)
      this._container?.parentNode.removeChild(this._container);
    this._map = undefined;
  }
}

class Props {
  height?: string = prop({
    required: false,
  });
  width?: string = prop({
    required: false,
  });
  markers?: MapMarker[] = prop({
    required: false,
  });
  markersInView?: MapMarker[] = prop({
    required: false,
  });
}

@Options({
  components: {
    Spinner,
    CustomSelect,
    CountrySelector,
    Logo,
    Tooltip,
  },
})
export default class Map extends Vue.with(Props) {
  map: MapLibre | null = null;
  initialState = { lng: 12.262935481151898, lat: 50.48130559363356, zoom: 3.8 }; // ~ middle of Europe
  libMakers: Marker[] = [];
  markersVisibles: MapMarker[] = [];
  userCoordinate: null | [number, number] = null;
  checkingMarkersVisibles = false;
  selectedCountry: ListItem | null = null;
  showGroupedMarkers = true;
  thresholdZoomForGrouping = 4.2;

  get groupMarkers(): MapMarker[] {
    const groups: MapMarker[] = [];
    this.markers?.forEach((marker) => {
      const group = groups.find(
        (group) => group.countryCode === marker.countryCode,
      );
      if (group) {
        group.groupSize = (group.groupSize || 1) + 1;
        if (marker.selected) {
          group.selected = true;
        }
        if (
          marker.provider &&
          group.provider &&
          Array.isArray(group.provider) &&
          !group.provider.includes(marker.provider as string) &&
          group.provider.length < 3
        ) {
          group.provider.push(marker.provider as string);
        }
        if (
          (!group.price && marker.price) ||
          (group.price && marker.price && marker.price < group.price)
        ) {
          group.price = marker.price;
        }
      } else {
        const country = countriesList.find(
          (country) => country.key === marker.countryCode,
        );
        groups.push({
          id: marker.countryCode ?? "",
          countryCode: marker.countryCode,
          lat: country?.latitude || this.initialState.lat,
          lng: country?.longitude || this.initialState.lng,
          city: country?.name || "",
          price: marker.price,
          currency: marker.currency,
          provider: [(marker.provider as string) ?? ""],
          selected: marker.selected || false,
          groupSize: 1,
        });
      }
    });
    return groups;
  }

  onSelectCountry(country: ListItem): void {
    this.selectedCountry = country;
    if (country && country.meta) {
      this.map?.flyTo({
        center: [country.meta.longitude, country.meta.latitude],
        zoom: this.thresholdZoomForGrouping + 0.1,
      });
    }
  }

  checkGrouping(): void {
    if (this.map) {
      if (this.map.getZoom() > this.thresholdZoomForGrouping) {
        if (this.showGroupedMarkers) {
          this.showGroupedMarkers = false;
        }
      } else {
        if (!this.showGroupedMarkers) {
          this.showGroupedMarkers = true;
        }
      }
    }
  }

  mountMap(): void {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition((position) => {
        this.userCoordinate = [
          position.coords.latitude,
          position.coords.longitude,
        ];
      });
    }
    this.map = new MapLibre({
      container: "map",
      style: "/map/style.json", // stylesheet location
      center: this.userCoordinate
        ? this.userCoordinate
        : [this.initialState.lng, this.initialState.lat], // starting position [lng, lat]
      zoom: this.initialState.zoom, // starting zoom
      maxZoom: 5,
      minZoom: 3,
    });

    const protocol = "custom://";
    this.map.setTransformRequest((url, resourceType) => {
      if (
        url.startsWith(protocol) &&
        (resourceType === "Tile" || resourceType === "Glyphs")
      ) {
        return { url: window.location.origin + url.split(protocol)[1] };
      }
      return undefined;
    });

    this.map.addControl(new NavigationControl(), "top-right");
    this.map.addControl(
      new GeolocateControl({
        fitBoundsOptions: {
          maxZoom: 4,
        },
        positionOptions: {
          enableHighAccuracy: false,
        },
        showAccuracyCircle: false,
        showUserLocation: false,
      }),
      "top-left",
    );

    this.map.addControl(
      new SearchCountryControl(this.$refs.searchCountry as HTMLElement),
      "top-left",
    );

    this.map.on("moveend", () => {
      this.checkMarkersVisibles();
    });
    this.map.on("zoom", () => {
      this.checkGrouping();
    });

    this.$nextTick(() => {
      if (this.userCoordinate) {
        this.map?.flyTo({ center: this.userCoordinate });
      }
    });
  }

  clearMarkers(): void {
    if (this.map) {
      this.libMakers.forEach((libMarker) => {
        libMarker.remove();
      });
      this.libMakers = [];
    }
  }

  mountMarkers(flyto?: boolean): void {
    if (this.map) {
      this.markers?.forEach((marker, index) => {
        const libMarker = new Marker({
          element: (this.$refs["map-marker-" + index] as any)[0] as HTMLElement,
        }).setLngLat([marker.lng, marker.lat]);
        if (this.map) libMarker.addTo(this.map);
        this.libMakers.push(libMarker);

        // If a marker is selected, move the map to this poistion
        if (marker.selected && flyto) {
          this.map?.flyTo({ center: [marker.lng, marker.lat] });
        }
      });

      this.checkMarkersVisibles();
    }
  }

  mountGroupMarkers(flyto?: boolean): void {
    if (this.map) {
      this.groupMarkers?.forEach((groupMarker, index) => {
        const libMarker = new Marker({
          element: (
            this.$refs["map-group-marker-" + index] as any
          )[0] as HTMLElement,
        }).setLngLat([groupMarker.lng, groupMarker.lat]);
        if (this.map) libMarker.addTo(this.map);
        this.libMakers.push(libMarker);

        // If a marker is selected, move the map to this poistion
        if (groupMarker.selected && flyto) {
          this.map?.flyTo({ center: [groupMarker.lng, groupMarker.lat] });
        }
      });
    }
  }

  mountAllMarkers() {
    this.clearMarkers();
    this.checkGrouping();
    this.mountMarkers(false);
    this.mountGroupMarkers(true);
  }

  checkMarkersVisibles() {
    if (!this.checkingMarkersVisibles) {
      this.checkingMarkersVisibles = true;
      const bounds = this.map?.getBounds();
      if (!bounds) return [];

      this.markersVisibles =
        this.markers?.filter((marker) => {
          return bounds.contains([marker.lng, marker.lat]);
        }) || [];

      this.$emit("update:markersInView", this.markersVisibles);
      this.checkingMarkersVisibles = false;
    }
  }

  mounted(): void {
    if (!this.map) {
      this.mountMap();
    }
    this.mountAllMarkers();
  }

  @Watch("markers")
  onMarkersChange() {
    this.$nextTick(() => {
      if (!this.map) {
        this.mountMap();
      }
      this.mountAllMarkers();
    });
  }
}
</script>

<style lang="scss" scoped>
@import "@/css/variables";
@import "~maplibre-gl/dist/maplibre-gl.css";
.map-wrap {
  position: relative;
  width: 100%;
  height: calc(
    100vh - 77px
  ); /* calculate height of the screen minus the heading */
}

.map {
  position: absolute;
  width: 100%;
  height: 100%;
  &:deep(.maplibregl-ctrl-top-left) {
    display: flex;
    .maplibregl-ctrl {
      clear: both;
      height: fit-content;
    }
  }

  .search-country-control {
    min-width: 250px;
    &:deep(.custom-select-input) {
      height: 30px;
      background: white;
    }
  }
}

.map-marker {
  pointer-events: none;
  .map-marker-wrapper {
    &.hidden {
      display: none;
    }
    &.disabled {
      .map-marker-background {
        position: absolute;
        top: 0;
        left: 0;
        right: 0;
        bottom: 0;
        width: 100%;
        height: 100%;
        z-index: -1;
        background: $color-neutral-grey-20;
        border-radius: 16px;
        cursor: not-allowed;
      }
      .map-marker-content {
        background: white;
        cursor: not-allowed;
        color: $color-neutral-grey-60;
      }
    }
    pointer-events: auto;
    transform: translate(0, -50%);
    .map-group-marker-size {
      z-index: 11;
      cursor: pointer;
      text-align: center;
      color: $color-neutral-black;
      background: $color-neutral-white-88;
      min-width: fit-content;
      width: 24px;
      height: 24px;
      font-size: 14px !important;
      line-height: 24px;
      border-radius: 16px;
      position: absolute;
      top: -8px;
      left: -8px;
      border: 1px solid $color-neutral-grey-60;
    }
    .map-marker-content {
      position: relative;
      z-index: 10;
      cursor: pointer;
      text-align: center;
      padding: 8px;
      color: $color-neutral-black;
      background: $color-neutral-white-88;
      border-radius: 16px;
      border: 1px solid $color-neutral-grey-60;
      &.map-marker-content-2 {
        top: -4px;
        left: 4px;
        position: absolute;
        z-index: 9;
        * {
          opacity: 0;
        }
      }
      &.map-marker-content-3 {
        top: -8px;
        left: 8px;
        position: absolute;
        z-index: 8;
        * {
          opacity: 0;
        }
      }
    }
    .map-marker-point {
      width: 8px;
      height: 8px;
      background: white;
      border: 1px solid $color-neutral-grey-60;
      border-radius: 8px;
    }
    &.selected {
      .map-marker-content {
        color: $color-grey;
        background: $color-brand;
        &.map-marker-content-2 {
          color: $color-neutral-black;
          background: $color-neutral-white-88;
        }
        &.map-marker-content-3 {
          color: $color-neutral-black;
          background: $color-neutral-white-88;
        }
      }
      .map-marker-point {
        background: $color-brand;
      }
    }
  }
}
</style>
